import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { castArray, isEmpty } from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import { validate as uuidValidate } from 'uuid';

import { toast } from '@hivebrite/design-system/Toast';

import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { money } from 'hb-react/shared/apps/Memberships/utils/money';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import * as errorReporting from 'hb-react/shared/utils/errorReporting';
import { polymorphicTopicUrl } from 'hb-react/shared/utils/polymorphicTopicUrl';

const membershipsTransformer = (memberships) => {
  const paymentOptions = memberships
    .flatMap(({ payment_options: paymentOptions = [] }) =>
      paymentOptions.find(({ selected }) => selected),
    )
    .filter(Boolean);

  if (paymentOptions.length > 0) {
    return paymentOptions;
  }

  return memberships;
};

const createCartRequest = ({ cart_items: cartItems }) =>
  axios.post(Routes.order_management_carts_v2_index_path(), {
    cart: {
      currency: cartItems[0].currency,
      cart_items: membershipsTransformer(cartItems)
        .flat()
        .filter(Boolean)
        .map(
          ({
            type,
            id,
            recurring_payment_enabled: recurring = false,
            price,
            quantity = 1,
          }) => ({
            type,
            total_in_cents: money(price).cents * quantity,
            line_itemable_id: id,
            quantity,
            recurring,
          }),
        ),
    },
  });

const wrapCartItems = (data) => {
  const {
    associated_items: { addons = [], donations = [], memberships = [] },
    cart,
  } = data;

  const { cart_items: cartItems = [] } = cart;

  const getCartItem = ({ type, id }) =>
    cartItems.find(
      (item) =>
        item.line_itemable_type === type && item.line_itemable_id === id,
    );

  const mapItem = (item) => ({
    ...item,
    cartItem: getCartItem(item),
  });

  return {
    ...data,
    associated_items: {
      ...data.associated_items,
      addons: addons.map(mapItem),
      donations: donations.map(mapItem),
      memberships: memberships.map((type) => ({
        ...type,
        payment_options: type.payment_options?.map(mapItem).map((opt) => ({
          ...opt,
          selected: opt.cartItem,
          disabled:
            !opt.is_purchasable ||
            (opt.price > 0 && opt.currency.code !== cart.currency.code),
        })),
        cartItem: getCartItem(type),
      })),
    },
  };
};

export const useShoppingCard = (id) => {
  const { cartId = id } = useParams();
  const navigate = useNavigate();
  const topicContext = useTopicContext();

  const queryClient = useQueryClient();

  const onSuccess = async ({ data }) => {
    const key = ['cart', data.cart.id];

    await queryClient.cancelQueries({
      queryKey: key,
    });

    queryClient.setQueryData(key, () => wrapCartItems(data));
  };

  const onError = (error) => {
    errorReporting.captureException(error);
    const errors = error?.response?.data?.errors_full_messages;
    const errorMessage = isEmpty(errors)
      ? __('Sorry, an error occurred')
      : errors;

    toast.error(castArray(errorMessage).join(' - '));
  };

  const onCreateError = (error) => {
    errorReporting.captureException(error);
    const errors = error?.response?.data?.errors_full_messages;
    const errorMessage = isEmpty(errors)
      ? __('Sorry, an error occurred')
      : errors;

    toast.error(castArray(errorMessage).join(' - '));
    const topicId = topicContext?.topicId;
    const networkId = topicContext?.networkId;

    navigate(
      polymorphicTopicUrl('membershipsIndex', {
        isTopic: !!topicId,
        urlParams: [networkId, topicId],
      }),
    );
  };

  const baseQueryOptions = {
    onError,
    onSuccess,
  };

  const { mutateAsync: create } = useMutation({
    mutationFn: createCartRequest,
    onError: (error) => onCreateError(error),
    onSuccess,
  });

  const { mutateAsync: updateCart } = useMutation({
    mutationFn: (cart) =>
      axios.put(Routes.order_management_carts_v2_path(cartId), {
        cart,
      }),
    ...baseQueryOptions,
  });

  const { mutateAsync: add } = useMutation({
    mutationFn: (...items) =>
      axios.post(Routes.order_management_carts_v2_cart_items_path(cartId), {
        cart: {
          cart_items: items,
        },
      }),
    ...baseQueryOptions,
  });

  const { mutateAsync: remove } = useMutation({
    mutationFn: (id) =>
      axios.delete(Routes.order_management_carts_v2_cart_item_path(cartId, id)),
    ...baseQueryOptions,
  });

  const { mutateAsync: update } = useMutation({
    mutationFn: (item) =>
      axios.patch(
        Routes.order_management_carts_v2_cart_item_path(cartId, item.id),
        {
          cart: { cart_items: [item] },
        },
      ),
    ...baseQueryOptions,
  });

  const { mutateAsync: complete } = useMutation({
    mutationFn: (payload = {}) =>
      axios.post(Routes.order_management_orders_path(), {
        cart_uuid: cartId,
        ...payload,
      }),
    onError,
  });

  const { mutateAsync: addPromoCode } = useMutation({
    mutationFn: (code) =>
      axios.post(
        Routes.validate_promo_code_order_management_carts_v2_path(cartId),
        { promo_code: { code } },
      ),
    onSuccess,
  });

  const {
    data = {
      cart: {},
    },
    isLoading,
  } = useQuery({
    queryKey: ['cart', cartId],
    queryFn: () =>
      axios
        .get(Routes.order_management_carts_v2_path(cartId))
        .then(({ data }) => wrapCartItems(data)),
    enabled: uuidValidate(cartId),
    refetchOnWindowFocus: false,
    staleTime: 300000,
  });

  const isMutating = useIsMutating();

  return {
    cartId,
    data,
    isLoading: Boolean(isLoading || isMutating),
    actions: {
      create,
      update: updateCart,
      complete,
      items: {
        update,
        remove,
        add,
        addPromoCode,
      },
    },
  };
};
